import React, { useEffect, useState, useContext } from "react";
import { Badge, Text, InlineStack, Tooltip, Avatar, ResourceItem, BlockStack } from "@shopify/polaris";
import { XSmallIcon, ImagesIcon } from "@shopify/polaris-icons";
import PropTypes from "prop-types";

// import from lib
import { baseHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import Popover from "lib/components/popover/popover";

const InvitationListView = (props) => {
  const {
    item,
    onCancel,
    onResend,
    cancelButtonLoading,
    resendButtonLoading,
    selectedButtonIndex,
    activePopover,
    setActivePopover,
  } = props;

  const { CANCEL, RESEND } = constant;

  const { _id, email, storeName, createdAt, emailStatus, status } = item;
  const { cms } = useContext(PrivateContext);
  const [selected, setSelected] = useState("");

  const pageContent = {
    label: {
      status: cms("label.status"),
      emailStatus: cms("label.emailStatus"),
      invitedBy: cms("label.invitedBy"),
      invitedOn: cms("label.invitedOn"),
      cancel: cms("button.cancel"),
      resend: cms("button.resend"),
    },
  };

  const badgeStatus = baseHelper.ucFirst(status);

  const badgeType = baseHelper.getBadgeType(status);

  const emailBadgeStatus = baseHelper.ucFirst(emailStatus);
  const emailBadgeType = baseHelper.getBadgeType(emailStatus);
  const media = <Avatar customer size="xl" name="" />;

  const handleSelectChange = (value, id) => {
    setSelected(value);
    if (value === CANCEL) {
      onCancel(email, id);
    }
    if (value === RESEND) {
      onResend(email, id);
    }
  };

  useEffect(() => {
    if (selected && (!resendButtonLoading || !cancelButtonLoading)) {
      setSelected("");
    }
  }, [cancelButtonLoading, resendButtonLoading, selected]);

  const topMargin10 = {
    marginTop: "10px",
  };

  return (
    <ResourceItem
      id={_id}
      media={media}
      accessibilityLabel={`${cms("label.viewDetailsFor")} ${storeName}`}
      persistActions
    >
      <InlineStack align="space-between">
        <BlockStack>
          <Text as="p" variant="headingSm">
            <div className="ellipsis">{email}</div>
          </Text>
          <Text variant="bodySm" as="p">
            <Text as="span">{`${pageContent.label.invitedOn}: ${baseHelper.formatDate(createdAt)}`}</Text>

            <br />
            <InlineStack gap="100" align="start">
              {`${pageContent.label.invitedBy}:`}
              <Text as="">{` ${storeName}`}</Text>
            </InlineStack>
          </Text>
        </BlockStack>
        <Popover
          // eslint-disable-next-line no-underscore-dangle
          active={activePopover[_id]}
          // eslint-disable-next-line no-underscore-dangle
          setActive={() => setActivePopover({ [_id]: !activePopover[_id] })}
          loading={_id === selectedButtonIndex && (resendButtonLoading || cancelButtonLoading)}
          disabled={status && status.toLowerCase() === constant.JOINED.toLowerCase()}
          options={[
            {
              content: `${pageContent.label.resend}`,
              icon: ImagesIcon,
              onAction: () => handleSelectChange(`${pageContent.label.resend}`, _id),
            },
            {
              content: `${pageContent.label.cancel}`,
              destructive: true,
              icon: XSmallIcon,
              onAction: () => handleSelectChange(`${pageContent.label.cancel}`, _id),
            },
          ]}
        />
      </InlineStack>
      <div style={topMargin10}>
        <InlineStack gap="400">
          <Tooltip content={pageContent.label.emailStatus} preferredPosition="above">
            <Badge tone={emailBadgeType}>
              <span className="text-capitalize" id="emailStatus">
                {emailBadgeStatus}
              </span>
            </Badge>
          </Tooltip>
          <Tooltip content={pageContent.label.status} preferredPosition="above">
            <Badge tone={badgeType}>
              <span className="text-capitalize" id="inviteStatus">
                {badgeStatus}
              </span>
            </Badge>
          </Tooltip>
        </InlineStack>
      </div>
    </ResourceItem>
  );
};

InvitationListView.defaultProps = {
  activePopover: false,
  cancelButtonLoading: false,
  handleCancel: () => {},
  handleResend: () => {},
  item: {},
  resendButtonLoading: false,
  selectedButtonIndex: "",
  setActivePopover: () => {},
};

InvitationListView.propTypes = {
  activePopover: PropTypes.bool,
  cancelButtonLoading: PropTypes.bool,
  handleCancel: PropTypes.func,
  handleResend: PropTypes.func,
  item: PropTypes.shape(PropTypes.object),
  resendButtonLoading: PropTypes.bool,
  selectedButtonIndex: PropTypes.number,
  setActivePopover: PropTypes.func,
};

export default InvitationListView;
